import { lowerCase, uniqueId } from 'lodash';

import { CHANNEL } from 'src/data/channel';
import { GRAPHQL_ERROR_CODES } from 'src/data/graphqlErrorCodes';
import { calculateImageDimensionsFile } from 'src/lib/browser';
import { SLATE_TEXT_FORMATS } from 'src/lib/slate';

import { EMAILS_CHANNELS_AND_RESOURCES } from './const';

export const GENERIC_RESOURCE = 'generic_resource';

export const EMAIL_CSV_RESOURCE = {
  emailSubjectLineCsvResource: 'email_subject_csv_resource',
  emailPromotionCsvResource: 'email_promotion_csv_resource',
  emailColdCsvResource: 'email_cold_csv_resource',
};

//TODO - CAN BE REMOVED?
export const RESOURCE_DOCUMENT = {
  ...EMAIL_CSV_RESOURCE,
  emailCsvResource: 'email_csv_resource',
  googleDocs: 'google_docs',
};

export const EMAIL_CSV_RESOURCES = Object.values(EMAIL_CSV_RESOURCE);

export const isEmailCsvResourceDocument = (resourceDocument) =>
  EMAIL_CSV_RESOURCES.includes(resourceDocument);

export const RESOURCE_DOCUMENT_PROPS = {
  [RESOURCE_DOCUMENT.emailCsvResource]: {
    label: 'Email performance report',
  },
  [RESOURCE_DOCUMENT.emailSubjectLineCsvResource]: {
    label: 'Email - subject line performance report',
  },
  [RESOURCE_DOCUMENT.emailPromotionCsvResource]: {
    label: 'Email - promotion performance report',
  },
  [RESOURCE_DOCUMENT.emailColdCsvResource]: {
    label: 'Email - cold performance report',
  },
  [RESOURCE_DOCUMENT.googleDocs]: {
    label: 'Google docs',
  },
};

export const RESOURCE_TAG = {
  general: 'general',
  amazon: 'amazon',
  trustpilot: 'trustpilot',
  homepage: 'home_page',
  productPage: 'product_page',
  landingPage: 'landing_page',
  competitors: 'competitors',
  thoughtLeadership: 'thought_leadership',
  blogPost: 'blog_post',
  generalDocument: 'general_document',
  generalImage: 'general_image',
};

export const RESOURCE_TAG_PROPS = {
  [RESOURCE_TAG.general]: {
    label: 'General',
  },
  [RESOURCE_TAG.amazon]: {
    label: 'Amazon reviews',
  },
  [RESOURCE_TAG.trustpilot]: {
    label: 'Trustpilot reviews',
  },
  [RESOURCE_TAG.homepage]: {
    label: 'Homepage',
  },
  [RESOURCE_TAG.productPage]: {
    label: 'Product information',
  },
  [RESOURCE_TAG.landingPage]: {
    label: 'Landing page',
  },
  [RESOURCE_TAG.competitors]: {
    label: 'Competitor',
  },
  [RESOURCE_TAG.thoughtLeadership]: {
    label: 'Thought leadership',
  },
  [RESOURCE_TAG.blogPost]: {
    label: 'Blog post',
  },
  [RESOURCE_TAG.generalDocument]: {
    label: 'General document',
  },
  [RESOURCE_TAG.generalImage]: {
    label: 'General image',
  },
};

export const getResourceTagLabel = (tag) => {
  return RESOURCE_TAG_PROPS[tag]?.label || tag;
};

export const RESOURCE_CONTENT_TYPE = {
  url: 'url',
  document: 'document',
  image: 'image',
  messagingBankAsset: 'messagingBankAsset',
  audio: 'audio',
  video: 'video',
};

export const VISIBLE_RESOURCE_CONTENT_TYPES = [
  RESOURCE_CONTENT_TYPE.url,
  RESOURCE_CONTENT_TYPE.document,
  RESOURCE_CONTENT_TYPE.image,
];

const MAX_IMAGE_DIMENSION = 6_000;

export const RESOURCE_FILE_UPLOAD_ERROR_CODE = {
  fileTooLarge: 'file-too-large',
  fileInvalidType: 'file-invalid-type',
  fileAlreadyExists: 'file-already-exists',
  fileExistsButNotAssociatedToWorkspace: 'file-exists-but-not-associated-to-workspace',
  imgResolutionExceeded: 'img-resolution-exceeded',
  tooManyFiles: 'too-many-files',
  notEnoughContent: 'not-enough-content',
};

const GRAPHQL_ERROR_TO_UPLOAD_ERROR_MAP = {
  [GRAPHQL_ERROR_CODES.resourceFileAlreadyExist]: RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists,
  [GRAPHQL_ERROR_CODES.resourceFileNotEnoughContent]:
    RESOURCE_FILE_UPLOAD_ERROR_CODE.notEnoughContent,
};

export const graphQLErrorCodeToResourceUploadErrorCode = (errorCode) => {
  return GRAPHQL_ERROR_TO_UPLOAD_ERROR_MAP[errorCode] || null;
};

export const RESOURCE_CONTENT_TYPE_PROPS = {
  [RESOURCE_CONTENT_TYPE.url]: {
    label: 'Webpage',
    nameInputPlaceholder: 'Acme Homepage',
  },
  [RESOURCE_CONTENT_TYPE.document]: {
    label: 'File',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this file',
    fileDescription: 'Supports PDF, DOCX, DOC, TXT & VVT files up to 50MB',
    fileMaxSize: 50_000_000,
    acceptedFileType: {
      'text/pdf': ['.pdf'],
      'text/vtt': ['.vtt'],
      'text/plain': ['.txt'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'File is too large for upload. The maximum allowed size is 50MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'File type is not supported. Please upload a valid file.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The document already exists in this account.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This document already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.notEnoughContent]: {
        title: "Upload failed. We couldn't find enough content in this file.",
      },
    },
  },
  [RESOURCE_CONTENT_TYPE.image]: {
    label: 'Image',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this image',
    fileDescription: 'Max image size 20MB (JPEG, JPG, PNG only)',
    fileMaxSize: 20_000_000,
    acceptedFileType: {
      'image/jpg': ['.jpg'],
      'image/jpeg': ['.jpeg'],
      'image/png': ['.png'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'The uploaded image is too big. The maximum file size allowed is 20MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'Upload failed. Only JPG, JPEG, and PNG formats are supported.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The image already exists in the workspace.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.imgResolutionExceeded]: {
        title: `Image upload failed. The maximum resolution supported is ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} pixels.`,
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This image already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
    },
  },
  [RESOURCE_CONTENT_TYPE.audio]: {
    label: 'Audio',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this audio',
    fileDescription: 'Supports MP3, WAV',
    fileMaxSize: 100_000_000,
    acceptedFileType: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'The uploaded audio is too big. The maximum file size allowed is 100MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'Upload failed. Only MP3 and WAV formats are supported.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The audio already exists in the workspace.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This audio already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
    },
  },
  [RESOURCE_CONTENT_TYPE.video]: {
    label: 'Video',
    nameInputPlaceholder: 'Enter name',
    referToPlaceholder: 'refer to this video',
    fileDescription: 'Supports MP4, AVI, MOV, MPEG',
    fileMaxSize: 100_000_000,
    acceptedFileType: {
      'video/mp4': ['.mp4'],
      'video/x-msvideo': ['.avi'],
      'video/quicktime': ['.mov'],
      'video/mpeg': ['.mpeg'],
    },
    errors: {
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileTooLarge]: {
        title: 'The uploaded video is too big. The maximum file size allowed is 100MB.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileInvalidType]: {
        title: 'Upload failed. Only MP4, AVI, MOV and MPEG formats are supported.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileAlreadyExists]: {
        title: 'Upload failed. The video already exists in the workspace.',
      },
      [RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace]: {
        title: 'Upload failed. This file already exists in another workspace.',
        description: 'Please go to the workspace settings to grant access and try again.',
      },
    },
  },
};

export const getResourceContentTypeLabel = (contentType) => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.label || contentType;
};

export const getResourceContentTypeReferToPlaceholder = (contentType) => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.referToPlaceholder || '';
};

export const getResourceContentTypeAccept = (contentType) => {
  return RESOURCE_CONTENT_TYPE_PROPS[contentType]?.acceptedFileType;
};

export const getResourceContentErrorMessage = (contentType, errorCode, options = null) => {
  const error = RESOURCE_CONTENT_TYPE_PROPS[contentType]?.errors?.[errorCode];
  if (!error) {
    return null;
  }

  if (options?.object) {
    return error;
  }

  return error.description ? `${error.title} ${error.description}` : error.title;
};

export const getResourceDocumentErrorMessage = (errorCode, options = null) =>
  getResourceContentErrorMessage(RESOURCE_CONTENT_TYPE.document, errorCode, options);
export const getResourceImageErrorMessage = (errorCode, options = null) =>
  getResourceContentErrorMessage(RESOURCE_CONTENT_TYPE.image, errorCode, options);

export const INCREASED_LARGE_RESOURCE_MAX_FILE_SIZE = 5_000_000_000; // 5GB

export const isLargeFileResourceType = (type) =>
  [RESOURCE_CONTENT_TYPE.video, RESOURCE_CONTENT_TYPE.audio].includes(type);

export const getResourceFileMaxSize = (resourceType) => {
  if (isLargeFileResourceType(resourceType)) {
    return INCREASED_LARGE_RESOURCE_MAX_FILE_SIZE;
  }
  return RESOURCE_CONTENT_TYPE_PROPS[resourceType]?.fileMaxSize;
};

export const getUploadingResourceHint = ({ resourceType, fileSize }) => {
  const getMinutes = () => {
    switch (resourceType) {
      case RESOURCE_CONTENT_TYPE.video:
      case RESOURCE_CONTENT_TYPE.audio:
        return fileSize > 500_000_000 ? 10 : 5; // 5 minutes for files up to 500MB, 10 minutes for files over 500MB

      default:
        return null;
    }
  };

  const minutes = getMinutes();
  let result = 'Your file is being uploaded and processed.';
  if (minutes) {
    result += `\nThis should take around ${minutes} minutes.`;
  }
  return result;
};

export const RESOURCE_STATUS = {
  active: 'active',
  processing: 'processing',
  archived: 'archived',
};

export const getResourceDocumentLabel = (document) => RESOURCE_DOCUMENT_PROPS[document]?.label;

export const getIsEmailChannel = (channel) =>
  EMAILS_CHANNELS_AND_RESOURCES.includes(channel) || channel === CHANNEL.emailChannel;
export const getIsResource = (channel) => EMAIL_CSV_RESOURCES.includes(channel);

export const getCreatedResourceMessage = (resource) => {
  return `A resource was successfully added to Anyword: "${resource?.name || resource?.url}"`;
};

export const getContentTypeTag = (contentType) => {
  switch (contentType) {
    case RESOURCE_CONTENT_TYPE.document:
      return RESOURCE_TAG.generalDocument;
    case RESOURCE_CONTENT_TYPE.image:
      return RESOURCE_TAG.generalImage;
    default:
      return RESOURCE_TAG.general;
  }
};

export const resourceChannelToContentType = (channel) => {
  if (channel === CHANNEL.other) {
    return RESOURCE_CONTENT_TYPE.url;
  }
  if (channel === CHANNEL.generalDocument) {
    return RESOURCE_CONTENT_TYPE.document;
  }
  return RESOURCE_CONTENT_TYPE.url;
};

export const isUrlResourceContentType = (contentType) => contentType === RESOURCE_CONTENT_TYPE.url;
export const isUrlResource = (resource) => isUrlResourceContentType(resource?.contentType);

export const isImageResourceContentType = (contentType) =>
  contentType === RESOURCE_CONTENT_TYPE.image;
export const isImageResource = (resource) => isImageResourceContentType(resource?.contentType);

export const isDocumentResource = (resource) =>
  resource?.contentType === RESOURCE_CONTENT_TYPE.document;
export const isAudioResource = (resource) => resource?.contentType === RESOURCE_CONTENT_TYPE.audio;
export const isVideoResource = (resource) => resource?.contentType === RESOURCE_CONTENT_TYPE.video;

// If we have "max file size" value for this resource type, the we can treat it as a "file" resource
export const isFileResourceContentType = (contentType) =>
  RESOURCE_CONTENT_TYPE_PROPS[contentType]?.fileMaxSize != null;
export const isFileResource = (resource) => isFileResourceContentType(resource?.contentType);

export const isNonImageFileResourceContentType = (contentType) =>
  isFileResourceContentType(contentType) && !isImageResourceContentType(contentType);
export const isNonImageFileResource = (resource) =>
  isNonImageFileResourceContentType(resource?.contentType);

export const RESOURCE_FILE_TYPE = {
  pdf: 'pdf',
  vtt: 'vtt',
  txt: 'txt',
  doc: 'doc',
  docx: 'docx',
};

export const isResourceFileTypePdf = (fileType) => lowerCase(fileType) === RESOURCE_FILE_TYPE.pdf;

export const fileTypeToExtension = (fileType) => `.${fileType}`;

export const validateImageResourceDimensions = (dimensionsObj) => {
  return Object.values(dimensionsObj).every((dimension) => dimension <= MAX_IMAGE_DIMENSION);
};

export const RESOURCE_UPLOAD_ID_PREFIX = {
  image: 'uploadImage',
  document: 'uploadDocument',
};
export const generateResourceUploadId = (resourceType) => {
  const prefix =
    resourceType === RESOURCE_CONTENT_TYPE.image
      ? RESOURCE_UPLOAD_ID_PREFIX.image
      : RESOURCE_UPLOAD_ID_PREFIX.document;
  return `${prefix}-${uniqueId()}`;
};

export const isResourceUploadId = (id) => {
  if (!id) {
    return false;
  }
  return (
    id.startsWith(RESOURCE_UPLOAD_ID_PREFIX.image) ||
    id.startsWith(RESOURCE_UPLOAD_ID_PREFIX.document)
  );
};

export const hasResourcesWithUploadId = (resources) =>
  resources.some((r) => isResourceUploadId(r.id));

export const enrichResourceFileInfo = async ({ resourceType, file }) => {
  if (resourceType !== RESOURCE_CONTENT_TYPE.image) {
    return file;
  }

  const dimensions = await calculateImageDimensionsFile(file);

  const newFile = Object.assign(file, {
    preview: URL.createObjectURL(file),
    dimensions,
  });

  if (!validateImageResourceDimensions(newFile.dimensions)) {
    throw new Error(
      getResourceContentErrorMessage(
        resourceType,
        RESOURCE_FILE_UPLOAD_ERROR_CODE.imgResolutionExceeded
      )
    );
  }

  return newFile;
};

/**
 * Mapping of resource content types to corresponding Slate text formats.
 * @constant {Object<string, string>}
 */
const RESOURCE_TYPE_TO_SLATE_TEXT_FORMAT = {
  [RESOURCE_CONTENT_TYPE.image]: SLATE_TEXT_FORMATS.PROMPT_IMAGE,
  [RESOURCE_CONTENT_TYPE.document]: SLATE_TEXT_FORMATS.PROMPT_DOCUMENT,
  [RESOURCE_CONTENT_TYPE.messagingBankAsset]: SLATE_TEXT_FORMATS.BANK_MESSAGE,
  [RESOURCE_CONTENT_TYPE.url]: SLATE_TEXT_FORMATS.RESOURCE_URL,
  [RESOURCE_CONTENT_TYPE.audio]: SLATE_TEXT_FORMATS.PROMPT_AUDIO,
  [RESOURCE_CONTENT_TYPE.video]: SLATE_TEXT_FORMATS.PROMPT_VIDEO,
};

/**
 * Reverse mapping of Slate text formats to resource content types.
 * @constant {Object<string, string>}
 */
const SLATE_TEXT_FORMAT_TO_RESOURCE_TYPE = Object.entries(
  RESOURCE_TYPE_TO_SLATE_TEXT_FORMAT
).reduce((acc, [resourceType, slateTextFormat]) => {
  acc[slateTextFormat] = resourceType;
  return acc;
}, {});

/**
 * Gets the resource content type corresponding to a given Slate text format.
 *
 * @param {string} slateTextFormat - The Slate text format.
 * @returns {string|null} The corresponding resource content type, or null if not found.
 */
export const getResourceContentTypeFromSlateTextFormat = (slateTextFormat) =>
  SLATE_TEXT_FORMAT_TO_RESOURCE_TYPE[slateTextFormat] ?? null;

/**
 * Gets the Slate text format corresponding to a given resource content type.
 *
 * @param {string} resourceType - The resource content type.
 * @returns {string|null} The corresponding Slate text format, or null if not found.
 */
export const getSlateTextFormatFromResourceContentType = (resourceType) =>
  RESOURCE_TYPE_TO_SLATE_TEXT_FORMAT[resourceType] ?? null;
